
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { namespace, Getter, Mutation, Action, State } from "vuex-class";
import { get } from "lodash";
import { FeaturesList } from "@/store/types";
import { GuardTour, GuardTourStep } from "@/store/guard-tours/types";
import { ILatLng, extend } from "@sureview/v2-mapping";
import { MapItemData, MapLayerData } from "@/store/map-layers/types";
import MapLayerItemTypes from "@/constants/MapLayerItemTypes";
import GoogleMap from "@/scripts/mapping/map";
import NavHeader from "@/components/NavHeader.vue";
import GuardTourSetupMap from "@/components/guard-tours/GuardTourSetupMap.vue";
import GuardTourAreaPicker from "@/components/guard-tours/GuardTourAreaPicker.vue";
import GuardTourStepEditor from "@/components/guard-tours/GuardTourStepEditor.vue";

const GuardTours = namespace("GuardTours");
const MapLayers = namespace("mapLayers");

@Component({
	components: {
		"nav-header": NavHeader,
		"guard-tour-setup-map": GuardTourSetupMap,
		"guard-tour-area-picker": GuardTourAreaPicker,
		"guard-tour-step-editor": GuardTourStepEditor,
	}
})
export default class GuardTourSetup extends Vue {
	public $refs!: {
		guardTourSetupMap: GuardTourSetupMap;
	};

	// Global store
	@Getter("getFeaturesList") featuresList: FeaturesList;

	// Map module
	@MapLayers.Action loadAreaMapLayersBounds;
	@MapLayers.Action loadMapLayersByGroupID;
	@MapLayers.Getter("getMapLayersBounds")	mapLayersBounds!: google.maps.LatLngBoundsLiteral;

	// GuardTours actions
	@GuardTours.Action fetchGuardTours;

	// GuardTours mutations
	@GuardTours.Mutation setCurrentGuardTour: (currentGuardTour: GuardTour) => void;
	@GuardTours.Mutation pushGuardTourStep: (guardTourStep: GuardTourStep) => void;
	@GuardTours.Mutation setCreatingWaypoint: (creating: boolean) => void;

	// GuardTours accessors
	@GuardTours.State guardTours: GuardTour[];
	@GuardTours.State currentGuardTour: GuardTour;
	@GuardTours.State creatingWaypoint: boolean;
	@GuardTours.State waypointMarkerPosition: string;
	@GuardTours.State(state => state.selectedAreaId) selectedAreaId: number;

	public showTourOnMap: boolean = false;
	public isTourUpdated: boolean = false;
	public mapItems: MapItemData[] = null;
	public map: GoogleMap | null = null;
	public numberOfWaypoints: number = 0;
	private visibleMapItems: MapItemData[] = [];

	private get isGuardToursEnabled(): boolean {
		return get(this.featuresList, ["Mobile", "GuardTours"]);
	}

	private get isGuardTourSelected() : boolean {
		if (this.currentGuardTour && this.currentGuardTour.guardTourSteps && this.currentGuardTour.guardTourSteps.length > 0) {
			return true;
		}
		return false;
	}

	public beforeDestroy() {
		this.setCurrentGuardTour(null);
		this.setCreatingWaypoint(false);
	}

	@Watch("selectedAreaId")
	public async onSelectedAreaIdChange(areaId) {
		this.mapItems = null;
		this.visibleMapItems = [];
		this.setCurrentGuardTour(null);

		// Move map bounds to currently selected area
		await this.loadAreaMapLayersBounds(this.selectedAreaId);
		if (this.mapLayersBounds) {
			this.map = this.$refs.guardTourSetupMap.map!;
			// Move map to area
			this.map!.map.fitBounds(
				new google.maps.LatLngBounds(
					{ lat: this.mapLayersBounds.south, lng: this.mapLayersBounds.west },
					{ lat: this.mapLayersBounds.north, lng: this.mapLayersBounds.east }
				)
			);
		}

		// Get the guard tours for the area
		this.fetchGuardTours(this.selectedAreaId);

		// Count how many waypoints are in the area
		this.numberOfWaypoints = 0;
		this.mapItems &&
			this.mapItems.forEach((item: MapItemData, index: number) => {
				if (item.mapLayerItemTypeID == MapLayerItemTypes.Waypoint) this.numberOfWaypoints++;
			});
	}	

	@Watch("waypointMarkerPosition")
	public async waypointClick(waypointLocation: string): Promise<void> {
		this.numberOfWaypoints++;

		let layers: MapLayerData[] = await this.loadMapLayersByGroupID(this.selectedAreaId);
		const currentElevation = this.$refs.guardTourSetupMap.elevation;

		let waypointLayerId: MapLayerData = null;
		if (layers.length > 1) {
			// If theres multiple layers avaliable on the map, assign the waypoint to the layer that matches the elevation
			waypointLayerId = layers.filter(
				(layer: MapLayerData) =>
					(layer.minElevation == null || layer.minElevation! <= currentElevation) &&
					(layer.maxElevation == null || layer.maxElevation! >= currentElevation)
			)[0];
		} else if (layers.length === 1) {
			// If theres only one layer avaliable on the map, assign the waypoint to that layer
			waypointLayerId = layers[0];
		}

		var mapLayerIds = waypointLayerId && waypointLayerId.mapLayerID ? [waypointLayerId.mapLayerID] : [];

		var tempWaypointMarker: MapItemData = {
			mapLayerItemID: 0,
			mapLayerItemTypeID: 101,
			minElevation: currentElevation,
			maxElevation: currentElevation,
			title: "Waypoint " + this.numberOfWaypoints,
			latLong: this.waypointMarkerPosition,
			objectID: null,
			extraValue: null,
			regionPath: null,
			mapLayerIds: mapLayerIds,
			saved: false,
			locationIdentifiers: [],
			brokenObjectLink: false
		};

		this.addItemToGuardTour(tempWaypointMarker);
		this.$refs.guardTourSetupMap.displayItemOnMap(tempWaypointMarker, "active");
	}	

	public toggleShowCheckpoints(): void {
		this.showTourOnMap = !this.showTourOnMap;
	}

	public toggleCreateWaypoints(): void {
		if (!this.creatingWaypoint) {
			this.$refs.guardTourSetupMap.map!.setCursor("crosshair");
			this.setCreatingWaypoint(true);
		} else {
			this.setCreatingWaypoint(false);
			this.$refs.guardTourSetupMap.map!.setCursor("grab");
		}
	}

	public addItemToGuardTour(item: MapItemData): void {
		if (item && this.currentGuardTour) {
			let newStep: GuardTourStep = {
				guardTourId: this.currentGuardTour.guardTourId,
				mapLayerItemTitle: item.title,
				mapLayerItemId: item.mapLayerItemID,
				mapLayerItemTypeId: item.mapLayerItemTypeID,
				latLong: item.latLong,
				mapLayerId: item.mapLayerIds ? item.mapLayerIds[0] : null,
				minElevation: item.minElevation,
				maxElevation: item.maxElevation,
				stepOrder: this.currentGuardTour.guardTourSteps.length + 1
			};

			this.pushGuardTourStep(newStep);
		}
	}

	private goToElevation(elevation: number) {
		this.$refs.guardTourSetupMap.gotoElevation(elevation);
	}

	private tourUpdatedOrCancelled() {
		this.isTourUpdated = !this.isTourUpdated;
	}

	private visibleMapItemsUpdated(mapItems: MapItemData[]) {
		this.visibleMapItems = mapItems;
	}
}
